/**
 * Payment validation utilities for IBAN, BIC, and Sort Code
 */

// IBAN validation
export const validateIBAN = (iban) => {
  if (!iban) return { isValid: true, error: '' };
  
  // Remove spaces and convert to uppercase
  const cleanIban = iban.replace(/\s/g, '').toUpperCase();
  
  // Check length (IBAN lengths vary by country: 15-34 characters)
  if (cleanIban.length < 15 || cleanIban.length > 34) {
    return { isValid: false, error: 'IBAN must be between 15 and 34 characters long' };
  }
  
  // Check format: 2-letter country code + 2 digits + alphanumeric characters
  const ibanPattern = /^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/;
  if (!ibanPattern.test(cleanIban)) {
    return { isValid: false, error: 'Invalid IBAN format. Must start with country code (2 letters) + 2 digits' };
  }
  
  // Basic country code check
  const countryCode = cleanIban.substring(0, 2);
  const validCountryCodes = [
    'AD', 'AE', 'AL', 'AT', 'AZ', 'BA', 'BE', 'BG', 'BH', 'BR', 'BY', 'CH', 'CR', 'CY', 'CZ', 'DE', 'DK', 'DO', 'EE', 'EG', 'ES', 'FI', 'FO', 'FR', 'GB', 'GE', 'GI', 'GL', 'GR', 'GT', 'HR', 'HU', 'IE', 'IL', 'IS', 'IT', 'JO', 'KW', 'KZ', 'LB', 'LC', 'LI', 'LT', 'LU', 'LV', 'MC', 'MD', 'ME', 'MK', 'MR', 'MT', 'MU', 'NL', 'NO', 'PK', 'PL', 'PS', 'PT', 'QA', 'RO', 'RS', 'SA', 'SE', 'SI', 'SK', 'SM', 'TN', 'TR', 'UA', 'VA', 'VG', 'XK', 'ZA'
  ];
  
  if (!validCountryCodes.includes(countryCode)) {
    return { isValid: false, error: 'Invalid country code in IBAN' };
  }
  
  return { isValid: true, error: '' };
};

// BIC validation
export const validateBIC = (bic) => {
  if (!bic) return { isValid: true, error: '' };
  
  const cleanBic = bic.replace(/\s/g, '').toUpperCase();
  
  // BIC format: 8 or 11 characters (9-11 for bank code + location + branch)
  if (cleanBic.length !== 8 && cleanBic.length !== 11) {
    return { isValid: false, error: 'BIC must be 8 or 11 characters long' };
  }
  
  // Check format: 4 letters (bank code) + 2 letters (country) + 2 alphanumeric (location) + 3 optional alphanumeric (branch)
  const bicPattern = cleanBic.length === 8 
    ? /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}$/
    : /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{5}$/;
  
  if (!bicPattern.test(cleanBic)) {
    return { isValid: false, error: 'Invalid BIC format. Must be 4 letters + 2 letters + 2 alphanumeric (+ 3 optional)' };
  }
  
  // Basic country code check
  const countryCode = cleanBic.substring(4, 6);
  const validCountryCodes = ['AE', 'AT', 'AU', 'BD', 'BE', 'BG', 'BH', 'BR', 'CH', 'CN', 'CU', 'CY', 'CZ', 'DE', 'DK', 'DO', 'EE', 'EG', 'ES', 'FI', 'FR', 'GB', 'GE', 'GI', 'GL', 'GR', 'GT', 'HK', 'HR', 'HU', 'ID', 'IE', 'IL', 'IN', 'IS', 'IT', 'JO', 'JP', 'KE', 'KG', 'KW', 'KZ', 'LB', 'LI', 'LK', 'LT', 'LU', 'LV', 'MC', 'MD', 'ME', 'MK', 'MO', 'MT', 'MU', 'MX', 'MY', 'NL', 'NO', 'NZ', 'OM', 'PA', 'PK', 'PL', 'PS', 'PT', 'QA', 'RO', 'RS', 'RU', 'SA', 'SE', 'SG', 'SI', 'SK', 'SM', 'TN', 'TR', 'UA', 'US', 'UZ', 'VA', 'VG', 'XK', 'ZA'];
  
  if (!validCountryCodes.includes(countryCode)) {
    return { isValid: false, error: 'Invalid country code in BIC' };
  }
  
  return { isValid: true, error: '' };
};

// Sort Code validation
export const validateSortCode = (sortCode) => {
  if (!sortCode) return { isValid: true, error: '' };
  
  const cleanSortCode = sortCode.replace(/[-\s]/g, '');
  
  // UK Sort Code: 6 digits, can have dashes
  if (!/^[0-9]{6}$/.test(cleanSortCode)) {
    return { isValid: false, error: 'Sort Code must be 6 digits' };
  }
  
  // Optional: check dash formatting
  if (sortCode.includes('-')) {
    if (!/^[0-9]{2}-[0-9]{2}-[0-9]{2}$/.test(sortCode)) {
      return { isValid: false, error: 'Sort Code must be formatted as XX-XX-XX' };
    }
  }
  
  return { isValid: true, error: '' };
};

// Format helpers
export const formatIBAN = (value) => {
  const cleaned = value.replace(/\s/g, '').toUpperCase();
  return cleaned.match(/.{1,4}/g)?.join(' ') || cleaned;
};

export const formatSortCode = (value) => {
  const cleaned = value.replace(/[-\s]/g, '');
  if (cleaned.length <= 2) return cleaned;
  if (cleaned.length <= 4) return cleaned.substring(0, 2) + '-' + cleaned.substring(2);
  return cleaned.substring(0, 2) + '-' + cleaned.substring(2, 4) + '-' + cleaned.substring(4, 6);
};
